import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

interface BlEcart { month: string; declared_bl: number; system_delivery: number; gap: number; bl_rows: number; }
interface MonthReport { client: string; from: string; to: string; received: Record<string, number>; delivered: Record<string, number>; stock_remaining: Record<string, number>; }
interface ClientReportResp { client_id: string; months: MonthReport[]; }
interface ClientMin { id: string; name: string; }
interface FrsRow { id: string; day: string; label: string; code: string; pm: number; gm: number; created_at: string; }

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtQty(rec: Record<string, number>): string {
  const keys = Object.keys(rec);
  if (!keys.length) return '—';
  return keys.map((k) => `${k}: ${rec[k]}`).join(', ');
}

export function Reports() {
  const toast = useToast();

  const [blMonth, setBlMonth] = useState(currentMonth());
  const [bl, setBl] = useState<BlEcart | null>(null);

  const [clients, setClients] = useState<ClientMin[] | null>(null);
  const [clientId, setClientId] = useState('');
  const [months, setMonths] = useState(currentMonth());
  const [report, setReport] = useState<MonthReport[] | null>(null);

  const [frs, setFrs] = useState<FrsRow[] | null>(null);
  const [frsSince, setFrsSince] = useState('');
  const [frsUntil, setFrsUntil] = useState('');

  useEffect(() => {
    api<ClientMin[]>('/v1/clients').then(setClients).catch(() => {});
  }, []);

  async function loadBl() {
    try {
      const d = await api<BlEcart>(`/v1/reports/bl-ecart?month=${encodeURIComponent(blMonth)}`);
      setBl(d);
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  async function loadClientReport() {
    if (!clientId) { toast('Choisissez un client', true); return; }
    try {
      const d = await api<ClientReportResp>(`/v1/clients/${clientId}/report?months=${encodeURIComponent(months)}`);
      setReport(d.months);
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  async function loadFrs() {
    try {
      const qs = new URLSearchParams();
      if (frsSince) qs.set('since', frsSince);
      if (frsUntil) qs.set('until', frsUntil);
      const d = await api<FrsRow[]>(`/v1/imports/frs?${qs.toString()}`);
      setFrs(d);
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  return (
    <>
      <div className="pagehead"><h1>Rapports</h1></div>

      <div className="card">
        <h2>Écart BL Marjane</h2>
        <div className="muted" style={{ marginBottom: '0.6rem' }}>
          Compare les dépôts déclarés par N° de BL (import) aux mouvements de livraison réellement enregistrés dans CaissePilot.
        </div>
        <label htmlFor="blm">Mois</label>
        <input id="blm" type="month" value={blMonth} onChange={(e) => setBlMonth(e.target.value)} />
        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button className="primary" onClick={loadBl}>Calculer</button>
        </div>
        {bl && (
          <div className="tblwrap" style={{ marginTop: '0.8rem' }}><table><tbody>
            <tr><th>Déclaré (BL)</th><th>Système (livraisons)</th><th>Écart</th><th>Lignes BL</th></tr>
            <tr>
              <td>{bl.declared_bl}</td>
              <td>{bl.system_delivery}</td>
              <td><span className={'badge ' + (bl.gap === 0 ? 'ok' : 'warn')}>{bl.gap}</span></td>
              <td>{bl.bl_rows}</td>
            </tr>
          </tbody></table></div>
        )}
      </div>

      <div className="card">
        <h2>Rapport client</h2>
        <label htmlFor="rc">Client</label>
        <select id="rc" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">— choisir —</option>
          {clients?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label htmlFor="rm">Mois (séparés par des virgules)</label>
        <input id="rm" value={months} onChange={(e) => setMonths(e.target.value)} placeholder="2026-07,2026-08,2026-09" />
        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button className="primary" onClick={loadClientReport}>Afficher</button>
        </div>
        {report && report.length > 0 && (
          <div className="tblwrap" style={{ marginTop: '0.8rem' }}><table><tbody>
            <tr><th>Mois</th><th>Reçu</th><th>Déposé</th><th>Stock restant</th></tr>
            {report.map((m, i) => (
              <tr key={i}>
                <td>{m.from} → {m.to}</td>
                <td>{fmtQty(m.received)}</td>
                <td>{fmtQty(m.delivered)}</td>
                <td>{fmtQty(m.stock_remaining)}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>

      <div className="card">
        <h2>Suivi fournisseurs importé (FRS)</h2>
        <div className="muted" style={{ marginBottom: '0.6rem' }}>
          Données brutes du suivi fournisseurs, affichées pour comparaison — aucun mouvement comptable n'y est lié.
        </div>
        <div className="row" style={{ gap: '12px' }}>
          <div>
            <label htmlFor="fs">Depuis</label>
            <input id="fs" type="date" value={frsSince} onChange={(e) => setFrsSince(e.target.value)} />
          </div>
          <div>
            <label htmlFor="fu">Jusqu'à</label>
            <input id="fu" type="date" value={frsUntil} onChange={(e) => setFrsUntil(e.target.value)} />
          </div>
        </div>
        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button onClick={loadFrs}>Afficher</button>
        </div>
        {frs && frs.length === 0 && <div className="muted" style={{ marginTop: '0.6rem' }}>Rien à afficher.</div>}
        {frs && frs.length > 0 && (
          <div className="tblwrap" style={{ marginTop: '0.8rem' }}><table><tbody>
            <tr><th>Date</th><th>Fournisseur</th><th>Code</th><th>PM</th><th>GM</th></tr>
            {frs.map((r) => (
              <tr key={r.id}>
                <td>{r.day}</td>
                <td>{r.label}</td>
                <td>{r.code}</td>
                <td>{r.pm}</td>
                <td>{r.gm}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>
    </>
  );
}
